"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";

import type { AuthStatus, IUser } from "@/types/auth";
import {
  clearAuthCookies,
  getAuthToken,
  getUserDataRaw,
  setAuthToken,
  setUserData,
  subscribeToAuthChanges,
} from "./cookies";

interface IAuthContextValue {
  user: IUser | null;
  token: string | null;
  isAuthenticated: boolean;
  status: AuthStatus;
  login: (token: string, tokenValidity: string, user: IUser) => void;
  logout: () => void;
  setUser: (user: IUser) => void;
}

const AuthContext = createContext<IAuthContextValue | null>(null);

const getServerSnapshot = () => null;
const getHydrationSnapshot = () => true;
const getHydrationServerSnapshot = () => false;

function subscribeToHydration(callback: () => void): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  const id = window.setTimeout(callback, 0);

  return () => window.clearTimeout(id);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const isReady = useSyncExternalStore(
    subscribeToHydration,
    getHydrationSnapshot,
    getHydrationServerSnapshot,
  );
  const token = useSyncExternalStore(
    subscribeToAuthChanges,
    () => getAuthToken(),
    getServerSnapshot,
  );
  const userJson = useSyncExternalStore(subscribeToAuthChanges, getUserDataRaw, getServerSnapshot);

  const user = useMemo<IUser | null>(() => {
    if (!userJson) return null;
    try {
      return JSON.parse(userJson) as IUser;
    } catch {
      return null;
    }
  }, [userJson]);

  const login = useCallback((nextToken: string, tokenValidity: string, nextUser: IUser) => {
    setAuthToken(nextToken, tokenValidity, nextUser);
  }, []);

  const logout = useCallback(() => {
    clearAuthCookies();
  }, []);

  const setUser = useCallback((nextUser: IUser) => {
    setUserData(nextUser);
  }, []);

  const status: AuthStatus = !isReady ? "loading" : token ? "authenticated" : "unauthenticated";

  const value = useMemo<IAuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated: status === "authenticated",
      status,
      login,
      logout,
      setUser,
    }),
    [user, token, status, login, logout, setUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): IAuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within <AuthProvider>");
  }
  return ctx;
}
